import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ArrowLeft, Calendar, Users, Trophy, MessageSquare, Zap, Star,
  Clock, MapPin, Plus, Send, ChevronRight, Loader2, RefreshCw,
  PartyPopper, Mic, Presentation, Heart, Palette, Handshake, Sparkles,
  Wifi, WifiOff
} from 'lucide-react';
import { toast } from 'sonner';
import { useWebSocket } from '@/hooks/useWebSocket';
import { BACKEND_URL, API } from '@/lib/backendUrl';


const TYPE_ICONS = {
  conference: Presentation, wedding: Heart, lobby_party: PartyPopper,
  concert: Mic, workshop: Sparkles, exhibition: Palette, meetup: Handshake,
};
const TYPE_COLORS = {
  conference: 'bg-blue-500/15 text-blue-400 border-blue-500/20',
  wedding: 'bg-pink-500/15 text-pink-400 border-pink-500/20',
  lobby_party: 'bg-[#F7931A]/15 text-[#F7931A] border-[#F7931A]/20',
  concert: 'bg-purple-500/15 text-purple-400 border-purple-500/20',
  workshop: 'bg-teal-500/15 text-teal-400 border-teal-500/20',
  exhibition: 'bg-indigo-500/15 text-indigo-400 border-indigo-500/20',
  meetup: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20',
};

function timeUntil(iso) {
  const diff = (new Date(iso).getTime() - Date.now()) / 1000;
  if (diff < 0) return 'Started';
  if (diff < 3600) return `${Math.ceil(diff / 60)}m`;
  if (diff < 86400) return `${Math.ceil(diff / 3600)}h`;
  return `${Math.ceil(diff / 86400)}d`;
}
function formatDt(iso) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/* ─── Events Tab ─── */
function EventsTab({ events, onRsvp, wallet, loading }) {
  const [showCreate, setShowCreate] = useState(false);
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold heading-font text-gray-200">Upcoming Events</h2>
        <Button size="sm" onClick={() => setShowCreate(!showCreate)} className="text-xs bg-[#F7931A] hover:bg-[#F7931A]/90 text-black" data-testid="create-event-btn">
          <Plus className="w-3 h-3 mr-1" />{showCreate ? 'Cancel' : 'Create Event'}
        </Button>
      </div>
      {showCreate && <CreateEventForm onCreated={() => { setShowCreate(false); }} wallet={wallet} />}
      {loading ? (
        <div className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[#F7931A]" /></div>
      ) : events.length === 0 ? (
        <p className="text-gray-600 text-center py-8 text-sm">No upcoming events. Create the first one!</p>
      ) : (
        <div className="space-y-3" data-testid="events-list">
          {events.map(ev => <EventCard key={ev.event_id} event={ev} onRsvp={onRsvp} wallet={wallet} />)}
        </div>
      )}
    </div>
  );
}

function EventCard({ event, onRsvp, wallet }) {
  const TypeIcon = TYPE_ICONS[event.event_type] || Calendar;
  const color = TYPE_COLORS[event.event_type] || TYPE_COLORS.meetup;
  const isAttending = event.attendees?.includes(wallet?.toLowerCase());
  const isFull = event.attendee_count >= event.max_attendees;
  return (
    <Card className="bg-white/[0.03] border-white/5" data-testid={`event-card-${event.event_id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={`text-[9px] ${color}`}>
              <TypeIcon className="w-2.5 h-2.5 mr-1" />{event.event_type}
            </Badge>
            {event.ticket_price_hermes > 0 && (
              <Badge variant="outline" className="text-[9px] border-[#F7931A]/30 text-[#F7931A] bg-[#F7931A]/5">
                {event.ticket_price_hermes} HERMES
              </Badge>
            )}
            {event.ticket_price_hermes === 0 && (
              <Badge variant="outline" className="text-[9px] border-emerald-500/30 text-emerald-400 bg-emerald-500/5">Free</Badge>
            )}
          </div>
          <span className="text-[10px] text-gray-600">{timeUntil(event.start_time)}</span>
        </div>
        <h3 className="text-sm font-semibold heading-font text-gray-100 mb-1">{event.title}</h3>
        <p className="text-[11px] text-gray-500 mb-3 line-clamp-2">{event.description}</p>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 text-[10px] text-gray-600">
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatDt(event.start_time)}</span>
            <span className="flex items-center gap-1"><Users className="w-3 h-3" />{event.attendee_count}/{event.max_attendees}</span>
          </div>
          <Button
            size="sm"
            disabled={isAttending || isFull || !wallet}
            onClick={() => onRsvp(event.event_id)}
            className={`text-xs h-7 ${isAttending ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/20' : 'bg-white/5 hover:bg-white/10 text-gray-300 border border-white/10'}`}
            data-testid={`rsvp-btn-${event.event_id}`}
          >
            {isAttending ? 'Attending' : isFull ? 'Full' : 'RSVP'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function CreateEventForm({ onCreated, wallet }) {
  const [form, setForm] = useState({ title: '', description: '', event_type: 'meetup', ticket_price_hermes: 0, max_attendees: 50 });
  const [submitting, setSubmitting] = useState(false);
  const handleSubmit = async () => {
    if (!form.title) return;
    setSubmitting(true);
    const start = new Date(Date.now() + 86400000).toISOString();
    const end = new Date(Date.now() + 86400000 + 7200000).toISOString();
    try {
      const res = await fetch(`${API}/api/metaverse/economy/events`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, host_wallet: wallet || '0x0000000000000000000000000000000000000000', start_time: start, end_time: end }),
      });
      if (!res.ok) throw new Error((await res.json()).detail);
      toast.success('Event created!');
      onCreated();
    } catch (e) { toast.error(e.message); }
    setSubmitting(false);
  };
  return (
    <Card className="bg-white/[0.03] border-[#F7931A]/10 mb-4" data-testid="create-event-form">
      <CardContent className="p-4 space-y-3">
        <Input placeholder="Event Title" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
          className="bg-white/[0.03] border-white/10 text-gray-100 text-sm" data-testid="event-title-input" />
        <Input placeholder="Description" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
          className="bg-white/[0.03] border-white/10 text-gray-100 text-sm" data-testid="event-desc-input" />
        <div className="flex gap-2">
          <select value={form.event_type} onChange={e => setForm(p => ({ ...p, event_type: e.target.value }))}
            className="flex-1 bg-white/[0.03] border border-white/10 rounded-md text-xs text-gray-300 px-3 py-2" data-testid="event-type-select">
            {Object.keys(TYPE_ICONS).map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <Input type="number" placeholder="Price (HERMES)" value={form.ticket_price_hermes}
            onChange={e => setForm(p => ({ ...p, ticket_price_hermes: parseFloat(e.target.value) || 0 }))}
            className="w-32 bg-white/[0.03] border-white/10 text-gray-100 text-sm" data-testid="event-price-input" />
        </div>
        <Button onClick={handleSubmit} disabled={submitting || !form.title} className="w-full text-xs bg-[#F7931A] text-black hover:bg-[#F7931A]/90" data-testid="submit-event-btn">
          {submitting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Plus className="w-3 h-3 mr-1" />}Create Event
        </Button>
      </CardContent>
    </Card>
  );
}

/* ─── Lobby Tab ─── */
function LobbyTab({ users, messages, onSendMessage, wallet, displayName }) {
  const [msg, setMsg] = useState('');
  const chatEndRef = useRef(null);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  const send = () => {
    if (!msg.trim()) return;
    onSendMessage(msg.trim());
    setMsg('');
  };
  return (
    <div className="grid md:grid-cols-3 gap-4">
      {/* Online users */}
      <div className="md:col-span-1">
        <h3 className="text-xs font-semibold text-gray-400 mb-3 flex items-center gap-1.5">
          <Users className="w-3.5 h-3.5" />Online ({users.length})
        </h3>
        <div className="space-y-2 max-h-80 overflow-y-auto" data-testid="online-users">
          {users.length === 0 ? (
            <p className="text-[10px] text-gray-600 text-center py-4">Nobody's here yet...</p>
          ) : users.map((u, i) => (
            <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/[0.02] border border-white/5">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-xs text-gray-300 truncate">{u.display_name}</span>
              <span className="text-[9px] mono text-gray-700 ml-auto">{u.wallet?.slice(0, 6)}</span>
            </div>
          ))}
        </div>
      </div>
      {/* Chat */}
      <div className="md:col-span-2">
        <h3 className="text-xs font-semibold text-gray-400 mb-3 flex items-center gap-1.5">
          <MessageSquare className="w-3.5 h-3.5" />Lobby Chat
        </h3>
        <div className="bg-white/[0.02] border border-white/5 rounded-xl p-3 h-72 overflow-y-auto mb-3" data-testid="chat-messages">
          {messages.length === 0 ? (
            <p className="text-[10px] text-gray-600 text-center py-12">No messages yet. Say hello!</p>
          ) : messages.map((m, i) => (
            <div key={i} className={`mb-2 ${m.wallet === wallet?.toLowerCase() ? 'text-right' : ''}`}>
              <span className="text-[9px] text-gray-600 mr-2">{m.display_name}</span>
              <span className="text-xs text-gray-300">{m.message}</span>
            </div>
          ))}
          <div ref={chatEndRef} />
        </div>
        <div className="flex gap-2">
          <Input value={msg} onChange={e => setMsg(e.target.value)} onKeyDown={e => e.key === 'Enter' && send()}
            placeholder="Type a message..." className="bg-white/[0.03] border-white/10 text-gray-100 text-xs" data-testid="chat-input" />
          <Button onClick={send} disabled={!msg.trim()} className="bg-[#F7931A] text-black hover:bg-[#F7931A]/90" data-testid="send-chat-btn">
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ─── Achievements Tab ─── */
function AchievementsTab({ profile, catalog }) {
  const earned = profile?.achievements || [];
  return (
    <div>
      {/* Level bar */}
      {profile && (
        <div className="bg-white/[0.03] border border-white/5 rounded-xl p-5 mb-6" data-testid="level-card">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#F7931A] to-[#B08D57] flex items-center justify-center font-bold heading-font text-black text-sm">
                {profile.level}
              </div>
              <div>
                <p className="text-sm font-semibold heading-font text-gray-100">Level {profile.level}</p>
                <p className="text-[10px] text-gray-500 mono">{profile.xp} / {profile.next_level_xp} XP</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-lg font-bold heading-font mono text-[#F7931A]">{profile.total_xp}</p>
              <p className="text-[9px] text-gray-600">Total XP</p>
            </div>
          </div>
          <div className="h-2 bg-white/5 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#F7931A] to-[#B08D57] rounded-full transition-all" style={{ width: `${profile.progress || 0}%` }} />
          </div>
        </div>
      )}
      {/* Achievement grid */}
      <h3 className="text-xs font-semibold text-gray-400 mb-3">Achievements ({earned.length}/{Object.keys(catalog).length})</h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="achievements-grid">
        {Object.entries(catalog).map(([key, ach]) => {
          const isEarned = earned.includes(key);
          return (
            <div key={key} className={`rounded-xl border p-3 text-center transition-all ${isEarned ? 'bg-[#F7931A]/5 border-[#F7931A]/20' : 'bg-white/[0.02] border-white/5 opacity-50'}`}
              data-testid={`achievement-${key}`}>
              <div className={`w-10 h-10 rounded-full mx-auto mb-2 flex items-center justify-center ${isEarned ? 'bg-[#F7931A]/20' : 'bg-white/5'}`}>
                <Star className={`w-5 h-5 ${isEarned ? 'text-[#F7931A]' : 'text-gray-600'}`} />
              </div>
              <p className={`text-[11px] font-semibold heading-font ${isEarned ? 'text-gray-100' : 'text-gray-500'}`}>{ach.title}</p>
              <p className="text-[9px] text-gray-600 mt-0.5">{ach.description}</p>
              <Badge variant="outline" className="text-[8px] mt-1.5 border-white/10 text-gray-500">{ach.xp} XP</Badge>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── XP Leaderboard Tab ─── */
function LeaderboardTab({ leaderboard }) {
  return (
    <div data-testid="xp-leaderboard">
      <h2 className="text-sm font-semibold heading-font text-gray-200 mb-4">XP Leaderboard</h2>
      {leaderboard.length === 0 ? (
        <p className="text-gray-600 text-center py-8 text-sm">No players yet. Be the first to earn XP!</p>
      ) : (
        <div className="space-y-2">
          {leaderboard.map(u => (
            <div key={u.wallet} className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${u.rank <= 3 ? 'bg-[#F7931A]/5 border-[#F7931A]/10' : 'bg-white/[0.02] border-white/5'}`}>
              <span className={`text-sm font-bold mono w-6 ${u.rank === 1 ? 'text-[#F7931A]' : u.rank === 2 ? 'text-gray-300' : u.rank === 3 ? 'text-amber-700' : 'text-gray-600'}`}>
                #{u.rank}
              </span>
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#F7931A]/30 to-[#B08D57]/20 flex items-center justify-center text-xs font-bold text-[#F7931A]">
                {u.level}
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-xs mono text-gray-300">{u.wallet.slice(0, 10)}...{u.wallet.slice(-6)}</span>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[9px] text-gray-600">{u.achievement_count} achievements</span>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-bold mono text-[#F7931A]">{u.total_xp}</p>
                <p className="text-[9px] text-gray-600">XP</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── MAIN PAGE ─── */
export default function MetaverseEconomyPage() {
  const [tab, setTab] = useState('events');
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [lobbyUsers, setLobbyUsers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [profile, setProfile] = useState(null);
  const [catalog, setCatalog] = useState({});
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wallet] = useState('0x92537BE4d02aa426F0BAB04F6300e9124f1B4Ac8');
  const [displayName] = useState('Explorer');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsR, eventsR, lobbyR, chatR, profileR, catalogR, lbR] = await Promise.all([
        fetch(`${API}/api/metaverse/economy/stats`),
        fetch(`${API}/api/metaverse/economy/events`),
        fetch(`${API}/api/metaverse/economy/lobby/online`),
        fetch(`${API}/api/metaverse/economy/lobby/chat`),
        fetch(`${API}/api/metaverse/economy/profile/${wallet}`),
        fetch(`${API}/api/metaverse/economy/achievements/catalog`),
        fetch(`${API}/api/metaverse/economy/leaderboard/xp`),
      ]);
      const [s, e, l, c, p, cat, lb] = await Promise.all([
        statsR.json(), eventsR.json(), lobbyR.json(), chatR.json(), profileR.json(), catalogR.json(), lbR.json(),
      ]);
      setStats(s);
      setEvents(e.events || []);
      setLobbyUsers(l.users || []);
      setMessages(c.messages || []);
      setProfile(p);
      setCatalog(cat.achievements || {});
      setLeaderboard(lb.leaderboard || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [wallet]);

  useEffect(() => { loadData(); }, [loadData]);

  // WebSocket — real-time lobby updates
  const handleWsMessage = useCallback((msg) => {
    if (msg.type === 'chat') {
      setMessages(prev => [...prev, msg.data].slice(-100));
    } else if (msg.type === 'presence') {
      if (msg.action === 'join') {
        setLobbyUsers(prev => {
          const exists = prev.some(u => u.wallet === msg.data.wallet);
          return exists ? prev : [...prev, { ...msg.data, online: true, last_seen: msg.timestamp }];
        });
      } else if (msg.action === 'leave') {
        setLobbyUsers(prev => prev.filter(u => u.wallet !== msg.data.wallet));
      }
    } else if (msg.type === 'init') {
      if (msg.data?.recent_messages) setMessages(msg.data.recent_messages);
    }
  }, []);

  const { connected: wsConnected } = useWebSocket('/api/ws/lobby', handleWsMessage);

  const handleRsvp = async (eventId) => {
    try {
      const res = await fetch(`${API}/api/metaverse/economy/events/${eventId}/rsvp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wallet }),
      });
      if (!res.ok) throw new Error((await res.json()).detail);
      toast.success('RSVP confirmed!');
      loadData();
    } catch (e) { toast.error(e.message); }
  };

  const sendMessage = async (message) => {
    try {
      const res = await fetch(`${API}/api/metaverse/economy/lobby/chat`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wallet, display_name: displayName, message }),
      });
      if (!res.ok) throw new Error('Failed');
      // If WS not connected, fetch manually
      if (!wsConnected) {
        const chatR = await fetch(`${API}/api/metaverse/economy/lobby/chat`);
        const c = await chatR.json();
        setMessages(c.messages || []);
      }
    } catch { toast.error('Failed to send'); }
  };

  // Join lobby on mount
  useEffect(() => {
    fetch(`${API}/api/metaverse/economy/lobby/join`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ wallet, display_name: displayName }),
    }).catch(() => {});
    return () => {
      fetch(`${API}/api/metaverse/economy/lobby/leave`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wallet, display_name: displayName }),
      }).catch(() => {});
    };
  }, [wallet, displayName]);

  return (
    <div className="min-h-screen bg-[#0a0e1a] text-gray-100" data-testid="metaverse-economy-page">
      {/* Nav */}
      <nav className="sticky top-0 z-50 border-b border-white/5 bg-[#0a0e1a]/80 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/metaverse" className="text-gray-400 hover:text-white transition-colors" data-testid="back-to-metaverse">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-[#F7931A] to-[#B08D57] flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <span className="heading-font text-base font-semibold tracking-tight">Metaverse Economy</span>
            </div>
            <Badge variant="outline" className={`text-[9px] ${wsConnected ? 'border-cyan-500/30 text-cyan-400 bg-cyan-500/5' : 'border-gray-600/30 text-gray-600 bg-gray-600/5'}`} data-testid="ws-lobby-status">
              {wsConnected ? <Wifi className="w-2.5 h-2.5 mr-1" /> : <WifiOff className="w-2.5 h-2.5 mr-1" />}
              {wsConnected ? 'Live' : 'Polling'}
            </Badge>
          </div>
          <Button size="sm" variant="outline" onClick={loadData} className="border-white/10 text-gray-400" data-testid="refresh-economy-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />Refresh
          </Button>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 mb-6" data-testid="economy-stats">
            <div className="bg-white/[0.03] border border-white/5 rounded-xl p-3 text-center">
              <p className="text-lg font-bold heading-font mono text-[#F7931A]">{stats.virtual_events}</p>
              <p className="text-[9px] text-gray-500">Events</p>
            </div>
            <div className="bg-white/[0.03] border border-white/5 rounded-xl p-3 text-center">
              <p className="text-lg font-bold heading-font mono text-gray-100">{stats.upcoming_events}</p>
              <p className="text-[9px] text-gray-500">Upcoming</p>
            </div>
            <div className="bg-white/[0.03] border border-white/5 rounded-xl p-3 text-center">
              <p className="text-lg font-bold heading-font mono text-emerald-400">{stats.online_users}</p>
              <p className="text-[9px] text-gray-500">Online</p>
            </div>
            <div className="bg-white/[0.03] border border-white/5 rounded-xl p-3 text-center">
              <p className="text-lg font-bold heading-font mono text-gray-100">{stats.total_gamers}</p>
              <p className="text-[9px] text-gray-500">Players</p>
            </div>
            <div className="bg-white/[0.03] border border-white/5 rounded-xl p-3 text-center">
              <p className="text-lg font-bold heading-font mono text-gray-100">{stats.total_chat_messages}</p>
              <p className="text-[9px] text-gray-500">Messages</p>
            </div>
            <div className="bg-white/[0.03] border border-white/5 rounded-xl p-3 text-center">
              <p className="text-lg font-bold heading-font mono text-[#F7931A]">{stats.total_xp_distributed}</p>
              <p className="text-[9px] text-gray-500">XP Given</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab} className="space-y-6">
          <TabsList className="bg-white/[0.03] border border-white/5" data-testid="economy-tabs">
            <TabsTrigger value="events" className="text-xs gap-1" data-testid="tab-events"><Calendar className="w-3 h-3" />Events</TabsTrigger>
            <TabsTrigger value="lobby" className="text-xs gap-1" data-testid="tab-lobby"><MessageSquare className="w-3 h-3" />Social Lobby</TabsTrigger>
            <TabsTrigger value="achievements" className="text-xs gap-1" data-testid="tab-achievements"><Trophy className="w-3 h-3" />Achievements</TabsTrigger>
            <TabsTrigger value="leaderboard" className="text-xs gap-1" data-testid="tab-leaderboard"><Zap className="w-3 h-3" />Leaderboard</TabsTrigger>
          </TabsList>

          <TabsContent value="events">
            <EventsTab events={events} onRsvp={handleRsvp} wallet={wallet} loading={loading} />
          </TabsContent>

          <TabsContent value="lobby">
            <LobbyTab users={lobbyUsers} messages={messages} onSendMessage={sendMessage} wallet={wallet} displayName={displayName} />
          </TabsContent>

          <TabsContent value="achievements">
            <AchievementsTab profile={profile} catalog={catalog} />
          </TabsContent>

          <TabsContent value="leaderboard">
            <LeaderboardTab leaderboard={leaderboard} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
